import { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';
import { PlickNotification } from '@interfaces/general';
import { usePreferencesFromStore } from '@store/actions/preferences';
import { shouldStopSharedAlarm } from '@utils/notificationSound';

interface NotificationContextProps {
  notifications: PlickNotification[];
  addNotification: (notification: PlickNotification) => void;
  removeNotification: (id: string, stopNotificationsSound?: boolean) => void;
}

const NotificationContext = createContext<NotificationContextProps>({
  notifications: [],
  addNotification: () => null,
  removeNotification: () => null,
});

export const useNotificationContext = () => {
  return useContext(NotificationContext);
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<PlickNotification[]>([]);
  const { audio: storedAudio } = usePreferencesFromStore();
  const sound = useRef(new Audio(storedAudio));

  const playSound = () => {
    const audio = sound.current;
    audio.loop = true;
    audio.play();
  };

  const stopSound = () => {
    const audio = sound.current;
    audio.loop = false;
    audio.pause();
    audio.currentTime = 0;
  };

  // Re-sincroniza el reproductor cuando el usuario cambia el sonido en Ajustes.
  // Sin esto, useRef conserva el Audio del primer render (el sonido que había al
  // cargar la página) y el sonido elegido no se aplica hasta recargar.
  useEffect(() => {
    const a = sound.current;
    if (!a) return;
    // Si hay una alarma sonando al cambiar el sonido, conserva la reproducción
    // con el sonido nuevo en vez de cortarla.
    const wasLooping = a.loop;
    a.src = storedAudio;
    if (wasLooping) {
      a.loop = true;
      a.play();
    }
  }, [storedAudio]);

  useEffect(() => {
    return () => {
      if (sound.current) stopSound();
    };
  }, []);

  // Desbloqueo de autoplay: los navegadores bloquean `audio.play()` programático hasta que el
  // usuario interactúa con la página. Sin esto, una alarma que se dispara mientras el comercio
  // solo OBSERVA el panel (p. ej. un pedido programado creado desde otro dispositivo) queda
  // silenciada. Al primer gesto, "primamos" el Audio compartido (play mudo + pause) para
  // habilitar reproducciones futuras. Beneficia a TODAS las alarmas (entrantes y programados).
  useEffect(() => {
    const unlock = () => {
      const a = sound.current;
      if (!a) return;
      const prevMuted = a.muted;
      a.muted = true;
      const p = a.play();
      const finalize = () => {
        a.pause();
        a.currentTime = 0;
        a.muted = prevMuted;
      };
      if (p && typeof p.then === 'function') {
        p.then(finalize).catch(() => {
          a.muted = prevMuted;
        });
      } else {
        finalize();
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const removeNotification = (id: string, stopNotificationsSound = false) => {
    setNotifications((prev) => {
      const remaining = prev.filter((item) => item.id !== id);
      // Silenciado colateral (Audio único compartido): detener la alarma solo si NINGUNA
      // notificación restante sigue sonando. Así "Ignorar" en una notificación no apaga la
      // alarma de otra que aún debe sonar (p. ej. ignorar un pedido de otra sucursal mientras
      // suena uno propio).
      if (stopNotificationsSound && shouldStopSharedAlarm(remaining)) stopSound();
      return remaining;
    });
  };

  const addNotification = (notification: PlickNotification) => {
    setNotifications((prev) => [...prev, notification]);
    if (!notification?.mute) playSound();

    if (notification?.autoCloseTimer) {
      setTimeout(() => {
        removeNotification(notification.id, !notification?.mute);
      }, notification.autoCloseTimer - 250);
    }
  };

  const notificationValues: NotificationContextProps = {
    notifications,
    addNotification,
    removeNotification,
  };

  return <NotificationContext.Provider value={notificationValues}>{children}</NotificationContext.Provider>;
};
